/**
 * The hierarchical registry behind the map's layer controls panel: every
 * user-facing layer (a "leaf") grouped for the layer list, each independently
 * toggleable / opacity-adjustable / swipable.
 *
 * Each leaf maps a stable key + label + swatch color to a set of deck.gl
 * layer-id substrings. The override wiring turns a deck.gl `layer.id` into a
 * leaf via `leafKeyForLayerId`, then applies visibility / opacity / swipe from
 * the layer-controls atoms. Substring matching tolerates the dynamic id
 * suffixes the real layers use (e.g. `usa-structures-layer-3`,
 * `county-chunk-7`, `weather-wow-events-layer-post`).
 */

export type LayerLeaf = {
  key: string;
  label: string;
  /** deck.gl `layer.id` substrings (matched case-insensitively). */
  idPatterns: string[];
  /** Legend chip color (hex/rgb). */
  swatch: string;
  /** Listed in the panel even when no matching layer is on the map (boundary
   * layers must always appear, default off). */
  alwaysListed?: boolean;
  /** Visibility is owned by an app atom (wired in useLayerState) rather than
   * the layer-controls visibility state -- the eye is the SINGLE control and
   * drives the app's own layer state, so the override must not fight it. */
  appControlled?: boolean;
  /** How the override hides this leaf's layers when it must be hidden (its
   * group is toggled off). Default: every matched layer gets `visible: false`.
   * Counties can't be hidden that way -- the county polygon chunks stay
   * `visible: true` because they also carry the incident county-highlight and
   * selection fills; only `stroked`/`pickable` render the boundaries. So this
   * narrows hiding to specific id patterns and the props that switch the
   * boundary rendering off, leaving unmatched layers (the incident stat
   * labels in `county-text-layer`) untouched. */
  hide?: { patterns: string[]; props: Record<string, unknown> };
};

export type LayerGroup = {
  key: string;
  label: string;
  children: LayerLeaf[];
  /** The event group is the swipe-clip target. */
  isEvent?: boolean;
};

export const LAYER_REGISTRY: LayerGroup[] = [
  {
    key: 'event',
    label: 'Event Layers',
    isEvent: true,
    // Every logical layer is its own leaf so it can be independently toggled /
    // opacity-adjusted / swiped. Current and historical ids fold into the SAME
    // per-model POST / extent leaf, so the swipable layers are the same
    // whether the user is on current or historical data. Order matters for
    // matching: `ipaws-post` MUST precede `ipaws-alert` (`ipaws-0` matches the
    // alert pattern `ipaws-`, while `ipaws-post-...` matches `ipaws-post`
    // first). The historical ids look like `weather-{model}-events-layer-post`
    // / `...-threats`, hence the per-model `{model}-events-layer-*` patterns.
    children: [
      {
        key: 'wow-post',
        label: 'WOW POST grid',
        idPatterns: ['wow-post', 'wow-events-layer-post'],
        swatch: '#d32f2f',
      },
      // Forecast and observed perimeters are separate leaves so the smaller
      // observed perimeter can be viewed without the forecast one drawn over
      // it. The historical threats layer is split the same way by
      // time_category, so each leaf owns its own historical id.
      {
        key: 'wow-forecast',
        label: 'WOW forecast perimeters',
        idPatterns: ['wow-forecast-perimeter', 'wow-events-layer-threats-forecast'],
        swatch: '#ef9a9a',
      },
      {
        key: 'wow-observed',
        label: 'WOW observed perimeters',
        idPatterns: ['wow-observed-perimeter', 'wow-events-layer-threats-observed'],
        swatch: '#ef5350',
      },
      {
        key: 'nifc-post',
        label: 'NIFC POST grid',
        idPatterns: ['nifc-post', 'nifc-events-layer-post'],
        swatch: '#e65100',
      },
      {
        key: 'nifc-extent',
        label: 'NIFC perimeters',
        // nifc-text-layer draws the fire-name labels next to the perimeters,
        // so it hides/dims/clips with them.
        idPatterns: ['nifc-perimeter', 'nifc-text-layer', 'nifc-events-layer-threats'],
        swatch: '#f57c00',
      },
      {
        key: 'canopy-post',
        label: 'Canopy POST grid',
        idPatterns: ['canopy-post', 'canopy-events-layer-post'],
        swatch: '#2e7d32',
      },
      {
        key: 'canopy-extent',
        label: 'Canopy swaths',
        idPatterns: ['canopy-swath-post', 'canopy-events-layer-threats'],
        swatch: '#66bb6a',
      },
      {
        key: 'ting-outages',
        label: 'Ting outage polygons',
        // Live layer id is `ting-outages-layer`; ting runs no POST model, so
        // its only historical id is the threats one.
        idPatterns: ['ting-outages', 'ting-events-layer-threats'],
        swatch: '#d62828',
      },
      {
        key: 'ipaws-post',
        label: 'IPAWS POST grid',
        idPatterns: ['ipaws-post'],
        swatch: '#f9a825',
      },
      {
        key: 'ipaws-alert',
        label: 'IPAWS alert areas',
        idPatterns: ['ipaws-'],
        swatch: '#ffca28',
      },
      // Incident (past-event) page section layers -- one per section, only the
      // selected section's layers are visible, so at most one section is
      // "present" at a time. Two ordering constraints:
      // `incident-nonresidential` MUST precede `incident-residential`
      // ('nonresidentialstructures' contains the substring 'residential'),
      // and `incident-declarations` MUST precede `incident-population` (the
      // declarations section renders its grid through the population layer,
      // producing ids like 'declarations-population-0-7-layer' that contain
      // both words).
      {
        key: 'incident-nonresidential',
        label: 'Non-Residential Structures',
        idPatterns: ['nonresidentialstructures'],
        swatch: '#455a64',
      },
      {
        key: 'incident-residential',
        label: 'Residential Structures',
        idPatterns: ['residential'],
        swatch: '#6d4c41',
      },
      {
        key: 'incident-declarations',
        label: 'Declarations',
        idPatterns: ['declarations'],
        swatch: '#00897b',
      },
      {
        key: 'incident-communications',
        label: 'Communications',
        idPatterns: ['communications'],
        swatch: '#3949ab',
      },
      {
        key: 'incident-population',
        label: 'Population',
        idPatterns: ['population'],
        swatch: '#8e24aa',
      },
    ],
  },
  {
    key: 'boundaries',
    label: 'Boundaries',
    children: [
      {
        key: 'counties',
        label: 'County Boundaries',
        idPatterns: ['county-chunk', 'county-viewport-chunk', 'county-text-layer'],
        swatch: '#0078ae',
        alwaysListed: true,
        appControlled: true,
        hide: {
          patterns: ['county-chunk', 'county-viewport-chunk'],
          props: { stroked: false, pickable: false },
        },
      },
      {
        key: 'regions',
        label: 'FEMA Regions',
        idPatterns: ['region-layer'],
        swatch: '#005288',
      },
      {
        key: 'tribal',
        label: 'Tribal Boundaries',
        idPatterns: ['tribal-geojson-layer'],
        swatch: '#00838f',
        alwaysListed: true,
        appControlled: true,
      },
    ],
  },
  {
    key: 'reference',
    label: 'Reference',
    children: [
      {
        key: 'structures',
        label: 'USA Structures',
        idPatterns: ['usa-structures-layer'],
        swatch: '#5d4037',
      },
    ],
  },
];

/** Set of leaf keys belonging to the event (swipe-clip) group. */
export const EVENT_LEAF_KEYS: ReadonlySet<string> = new Set(
  LAYER_REGISTRY.filter(g => g.isEvent)
    .flatMap(g => g.children)
    .map(l => l.key)
);

const ALL_LEAVES: LayerLeaf[] = LAYER_REGISTRY.flatMap(g => g.children);

const LEAF_BY_KEY = new Map(ALL_LEAVES.map(l => [l.key, l]));

/** The registry leaf for a key, or `null` if no leaf has it. */
export function leafForKey(leafKey: string): LayerLeaf | null {
  return LEAF_BY_KEY.get(leafKey) ?? null;
}

/**
 * Map a deck.gl `layer.id` to its registry leaf key, or `null` if no leaf
 * claims it. Matching is case-insensitive substring containment so dynamic id
 * suffixes still resolve to their leaf.
 */
export function leafKeyForLayerId(id: string): string | null {
  const lower = id.toLowerCase();
  for (const leaf of ALL_LEAVES) {
    if (leaf.idPatterns.some(p => lower.includes(p.toLowerCase()))) {
      return leaf.key;
    }
  }
  return null;
}

/** The group key that owns a leaf, or `null`. */
export function groupKeyForLeaf(leafKey: string): string | null {
  return LAYER_REGISTRY.find(g => g.children.some(l => l.key === leafKey))?.key ?? null;
}

/** True when a leaf belongs to the event (swipe-clip) group. */
export function isEventLeaf(leafKey: string): boolean {
  return EVENT_LEAF_KEYS.has(leafKey);
}

/**
 * Whether a leaf's layers draw under the layer-controls visibility records:
 * its group AND its own eye must be on (a missing entry means on). For an
 * app-controlled leaf (county / tribal boundaries) the eye writes the app's
 * own visibility atom, so the layer already renders that state and only the
 * group answers here -- a stale persisted entry must not fight the app atom.
 */
export function isLeafShownIn(
  leafKey: string,
  leafVisibility: Record<string, boolean>,
  groupVisibility: Record<string, boolean>
): boolean {
  const leaf = leafForKey(leafKey);
  const groupKey = groupKeyForLeaf(leafKey);
  const groupHidden = groupKey != null && groupVisibility[groupKey] === false;
  const leafHidden = leaf != null && !leaf.appControlled && leafVisibility[leafKey] === false;
  return !groupHidden && !leafHidden;
}

/**
 * Whether a leaf participates in the swipe clip given the current membership
 * map. Only event leaves can participate; for those, a missing entry counts as
 * included (so default = swipe all event layers). Non-event leaves are never in
 * the swipe set.
 */
export function isLayerInSwipeSet(leafKey: string, swipeLayers: Record<string, boolean>): boolean {
  if (!isEventLeaf(leafKey)) return false;
  return swipeLayers[leafKey] !== false;
}
